import React, { useEffect, useMemo, useState } from "react";
import { AssignmentReturn } from "@mui/icons-material";
import {
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Tooltip,
	Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import CloseIcon from "@mui/icons-material/Close";
import PATCloseButton from "../../../components/Buttons/PATCloseButton";
import PATSaveButton from "../../../components/Buttons/PATSaveButton";
import FormSection from "../../../components/Form/FormSection";
import { useTheme } from "@emotion/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import participantSchema from "../../Training/Participants/participantSchema";
import moment from "moment";
import { getParticipantFields } from "../../Training/Participants/participantFieldConfigs";
import { getEmployeeOptionLabel } from "../../../utils/functions";
import { entityNamesForCustomId, serviceNames } from "../../../utils/constants";
import { useDispatch, useSelector } from "react-redux";
import { getService } from "../../../reducers/service";
import { notify } from "../../../reducers/NotifierReducer";
import { formatSaveDate } from "../../../reducers/TimeReducer";
import {
	createTrainingParticipant,
	updateTrainingParticipant,
} from "../../../reducers/TrainingParticipantReducer";

const buildDefaults = (data, employeeNum = "") => ({
	id: data?.id || null,
	employeeNum: data?.employeeNum || employeeNum || "",
	firstName: data?.firstName || "",
	lastName: data?.lastName || "",
	course: data?.course || "",
	courseNumber: data?.courseNumber || "",
	courseDate: data?.courseDate
		? moment(data.courseDate).format("MM/DD/YYYY")
		: "",
	attendedDate: data?.attendedDate
		? moment(data.attendedDate).format("MM/DD/YYYY")
		: "",
	courseLocation: data?.courseLocation || "",
	dateRegistered: data?.dateRegistered
		? moment(data.dateRegistered).format("MM/DD/YYYY")
		: "",
	hours: data?.hours || "",
	grade: data?.grade || "",
	approved: data?.approved || false,
	checkSent: data?.checkSent || false,
	dateCheckSent: data?.dateCheckSent || "",
	category: data?.category || "",
	approvedBy: data?.approvedBy || "",
	comments: data?.comments || "",
	participantNumber: data?.participantNumber || "",
});

const AddEditTrainingRecordDialog = ({
	open,
	initialData,
	employeeNum,
	onClose,
	onSave,
}) => {
	const id = initialData?.id;
	const theme = useTheme();
	const dispatch = useDispatch();
	const user = useSelector((state) => state.user);
	const userAgency = useSelector((state) => state.user.userAgency);

	const [loaded, setLoaded] = useState(false);
	const [locationOptions, setLocationOptions] = useState([]);
	const [categoryOptions, setCategoryOptions] = useState([]);
	const [employees, setEmployees] = useState([]);
	const [courses, setCourses] = useState([]);

	const defaultValues = useMemo(
		() => buildDefaults(initialData, employeeNum),
		[initialData, employeeNum],
	);

	const {
		control,
		handleSubmit,
		setValue,
        reset,
		watch,
		formState: { errors },
	} = useForm({
		resolver: yupResolver(participantSchema),
		defaultValues,
	});

	const loadOptions = async () => {
		setLoaded(false);
		try {
			const services = {
				distinctService: getService(
					serviceNames.trainingCourseDistinct,
				),
				employees: getService(serviceNames.personnel),
				courses: getService(serviceNames.trainingCourse),
				participants: getService(serviceNames.perTraining),
			};

			const results = await Promise.all([
				services.distinctService.find({
					query: {
						column: ["location", "category"],
						agencyId: userAgency,
					},
				}),
				services.employees.find({
					query: {
						$limit: -1,
						isDeleted: false,
						$sort: { lastName: 1, firstName: 1, middleName: 1 },
						agencyId: userAgency,
					},
				}),
				services.courses.find({
					query: {
						$limit: -1,
						isDeleted: false,
						agencyId: userAgency,
					},
				}),
			]);

			setLocationOptions(results[0]?.location);
			setCategoryOptions(results[0]?.category);
			setEmployees(results[1]);
			setCourses(results[2]);
			setLoaded(true);
		} catch (e) {
			setLoaded(true);
		}
	};

	useEffect(() => {
		if (open && initialData) {
			reset(buildDefaults(initialData, employeeNum));
		}
	    // eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialData, employeeNum, open, reset]);

	useEffect(() => {
		if (open) loadOptions();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	const options = {
		courseLocation: locationOptions,
		category: categoryOptions,
		employeeNum: employees.map((emp) => ({
			label: getEmployeeOptionLabel(emp),
			value: emp.employeeNum,
		})),
		courseNumber: courses.map((course) => ({
			label: `${course.courseNumber} - ${course.courseName}`,
			value: course.courseNumber,
		})),
	};

	const sectionProps = {
		control,
		errors,
		options,
		watch,
	};

	const generateCustomId = async () => {
		const data = {
			EntityName: entityNamesForCustomId.ParticipantId,
			agencyID: user.userAgency,
		};
		const service = getService(serviceNames.customId);
		service.timeout = 20000;

		const id = await service.create(data);
		if (id?.includes("Agency ID does not match")) {
			dispatch(
				notify(
					"Unable to generate Participant ID because the Agency ID does not match.",
					"warning",
				),
			);
			return null;
		}
		return id;
	};

	const handleAutoGenerateClick = async () => {
		const id = await generateCustomId();
		setValue("participantNumber", id);
	};

	const onSubmit = async (data) => {
		const participantId =
			data?.participantNumber?.trim() || (await generateCustomId());
		const cleanedData = {
			employeeNum: data?.employeeNum || null,
			lastName: data?.lastName?.trim() || null,
			firstName: data?.firstName?.trim() || null,
			course: data?.course || null,
			courseNumber: data?.courseNumber || null,
			courseDate: data?.courseDate
				? formatSaveDate(data.courseDate)
				: null,
			attendedDate: data?.attendedDate
				? formatSaveDate(data.attendedDate)
				: null,
			courseLocation: data?.courseLocation || null,
			dateRegistered: data?.dateRegistered
				? formatSaveDate(data.dateRegistered)
				: null,
			hours:
				data?.hours !== "" && !isNaN(Number(data?.hours))
					? Number(data?.hours)
					: null,
			grade: data?.grade?.trim() || null,
			approved: data?.approved ?? null,
			checkSent: data?.checkSent ?? null,
			dateCheckSent: data?.dateCheckSent
				? formatSaveDate(data.dateCheckSent)
				: null,
			category: data?.category?.trim() || null,
			approvedBy: data?.approvedBy?.trim() || null,
			agencyId: user.userAgency,
			participantNumber: data?.participantNumber || participantId,
			comments: data?.comments || "",
		};

		if (id) {
			await dispatch(updateTrainingParticipant(data.id, cleanedData));
		} else {
			await dispatch(createTrainingParticipant(cleanedData));
		}

		onSave?.();
		onClose?.();
	};

	return (
		<>
			<Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
				<DialogTitle
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
					}}
				>
					<Typography variant="h6">
						{id ? "Edit Training Record" : "Add Training Record"}
					</Typography>
					<IconButton
						aria-label="close"
						onClick={onClose}
						size="large"
					>
						<CloseIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent dividers>
					{!loaded ? (
						<Box
							display="flex"
							justifyContent="center"
							alignItems="center"
							minHeight={200}
						>
							<CircularProgress />
						</Box>
					) : (
						<Box sx={{ pt: 1 }}>
							<FormSection
								title="Training Record Information"
								icon={null}
								fields={getParticipantFields(!!id)}
								isExpandFunctionality={false}
								editPermission={true}
								theme={theme}
								isEdit={!!id}
								renderIdGenerationButton={() => (
									<Box>
										<Tooltip title="Auto generate certificate number">
											<IconButton
												color="primary"
												aria-label="Auto generate certificate number"
												size="large"
												onClick={
													handleAutoGenerateClick
												}
											>
												<AssignmentReturn />
											</IconButton>
										</Tooltip>
									</Box>
								)}
								{...sectionProps}
							/>
						</Box>
					)}
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<PATCloseButton onClick={onClose} tooltipText="Close" />
					<PATSaveButton
						onClick={handleSubmit(onSubmit)}
						tooltipText="Save Certification"
					/>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default AddEditTrainingRecordDialog;
